import * as React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import Divider from "@mui/material/Divider";

import EventEnumRadioSelector from "../../components/EventEnumRadioSelector";
import NewRadioSelector from "../../components/NewRadioSelector";
import { useSearch } from "../../contexts/searchContext";
import { GenerateCharacteristicsForSelector } from "./CharacteristicsGenerator";

function CharacteristicsSelector() {
  const {
    enumMap,
    setEnumMap,
    searchString,
    setSearchString,
    selectedCharacteristics,
    selectedDataset,
    setSelectedDataset,
    datasets,
    datasetMap,
    setDatasetMap
  } = useSearch();

  const characteristicsSchema = GenerateCharacteristicsForSelector(selectedDataset);

  // Filter out Dataset from characteristics list
  const characteristics = selectedCharacteristics.filter(char => char !== "Dataset");

  // Only restore enumMap from datasetMap when selectedDataset changes
  React.useEffect(() => {
    const datasetEnumMap = datasetMap.get(selectedDataset);
    if (datasetEnumMap && datasetEnumMap.size > 0) {
      setEnumMap(datasetEnumMap);
    }
  }, [selectedDataset, datasetMap, setEnumMap]);

  const handleCharacteristicEvent = React.useCallback((event, enumType, enumValue) => {
    const newEnumMap = new Map(enumMap);

    if (enumValue === null || enumValue === undefined) {
      newEnumMap.delete(enumType);
    } else {
      newEnumMap.set(enumType, enumValue);
    }

    datasetMap.set(selectedDataset, newEnumMap);
    setDatasetMap(datasetMap);
    setEnumMap(newEnumMap);
  }, [enumMap, datasetMap, selectedDataset, setDatasetMap, setEnumMap]);

  const handleDatasetEvent = React.useCallback((event, enumType, enumValue) => {
    const newCharacteristicsSchema = GenerateCharacteristicsForSelector(enumValue);
    const editedMap = editEnumMap(datasetMap.get(enumValue), newCharacteristicsSchema);

    setSelectedDataset(enumValue);
    setEnumMap(editedMap);
  }, [selectedDataset, datasetMap, setSelectedDataset, setEnumMap]);

  const handleSearchChange = React.useCallback((event) => {
    const value = event.target.value;
    setSearchString(value);
  }, [setSearchString]);

  const handleClearSearch = React.useCallback(() => {
    setSearchString("");
  }, [setSearchString]);

  const editEnumMap = (enumMap, newCharacteristicsSchema) => {
    const editedMap = new Map();
    for (const characteristic of Object.keys(newCharacteristicsSchema)) {
      const currentValue = enumMap.get(characteristic);
      if (currentValue && newCharacteristicsSchema[characteristic].includes(currentValue)) {
        editedMap.set(characteristic, currentValue);
      }
    }
    return editedMap;
  };

  const getInitialValue = (characteristic) => {
    return enumMap.has(characteristic) ? enumMap.get(characteristic) : null;
  };

  return (
    <Box sx={{ p: 1 }}>
      <Stack spacing={1}>
        {/* Search Field */}
        <TextField
          label="Filter by Name or Constituency"
          variant="outlined"
          value={searchString}
          onChange={handleSearchChange}
          size="small"
          fullWidth
          sx={{
            maxWidth: 400,
            '& .MuiOutlinedInput-root': {
              transition: 'all 0.2s',
              '&:hover': {
                boxShadow: 1,
              },
              '&.Mui-focused': {
                boxShadow: 2,
              }
            }
          }}
          InputProps={{
            endAdornment: searchString && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                  sx={{
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    }
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Divider />

        {/* Dataset Selector */}
        <Box>
          <EventEnumRadioSelector
            enumType="Dataset"
            enumList={datasets}
            initValue={selectedDataset}
            onEvent={handleDatasetEvent}
            selectionRequired={true}
            selectorSize={50}
            disabled={false}
          />
        </Box>

        <Divider />

        {/* Characteristics Selectors */}
        <Stack spacing={0.75}>
          {characteristics.map((characteristic) => (
            <NewRadioSelector
              key={characteristic}
              enumType={characteristic}
              enumList={characteristicsSchema[characteristic]}
              initValue={getInitialValue(characteristic)}
              onEvent={handleCharacteristicEvent}
              selectionRequired={false}
              selectorSize={50}
              disabled={false}
              enumMap={enumMap}
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}

// Memoize to prevent re-renders during search/filtering operations
export default React.memo(CharacteristicsSelector);
